import React from 'react';
import { Box, Paper, Grid, Typography, Tooltip } from '@mui/material';

// export default function FinancialMetricsTable(completionList: any, selectedCurrency: any) {
//   const isNegative = (val: any) => val.trim().startsWith('-');

export default function FinancialMetricsTable({ completionList, symbol }: { completionList: any; symbol: string }) {
  const isNegative = (val) => typeof val === 'string' && val.trim().startsWith('-');

  // const tableData = React.useMemo(() => transformIncomeData(completionList), [completionList]);

  const tableData = React.useMemo(() => transformIncomeData({ completionList }, symbol), [completionList]);

  if (!tableData) {
    return <p>No data available</p>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: '100%', // Force it to match your parent card's height restriction
        overflowY: 'auto', // Enable vertical scrollbar when content is too tall
        overflowX: 'hidden', // Block unwanted side-to-side scrolling shifts
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1a237e',
        p: 0.5,
        boxSizing: 'border-box',
        color: '#fff',
        fontSize: 12,

        // OPTIONAL: Add a clean modern scrollbar look matching the blue theme
        '&::-webkit-scrollbar': {
          width: '5px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#1a237e',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#e0e0e0',
          borderRadius: '4px',
        },
      }}
    >
      {/* Top Header Section */}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={4.5}>
          <Tooltip
            placement="bottom-end"
            title="Amount of the Purchase Order coming from the client"
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: 13,
                  padding: '4px 6px',
                  borderRadius: '6px',
                },
              },
            }}
          >
            <Box sx={{ backgroundColor: '#ffffff', color: '#1a237e', p: 0.6, fontWeight: 'bold', fontSize: 12 }}>
              Purchased (contractual)
            </Box>
          </Tooltip>
        </Grid>
        <Grid item xs={7.5}>
          <Box
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#1a237e',
              p: 0.6,
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: 14,
            }}
          >
            {tableData.purchasedTotal}
          </Box>
        </Grid>
      </Grid>

      {/* Column Group Headings */}
      <Grid container spacing={0.5} sx={{ m: 0.1, textAlign: 'center' }}>
        <Grid item xs={4.5} />
        <Grid item xs={3.75}>
          <Typography variant="caption" sx={{ color: '#fff', fontSize: 12 }}>
            TO DATE
          </Typography>
        </Grid>
        <Grid item xs={3.75}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#fff', fontSize: 12 }}>
            At Completion
          </Typography>
        </Grid>
      </Grid>

      {/* Core Operational Financial Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {tableData.rows.map((row) => (
          <Grid container spacing={1} alignItems="center" key={row.id}>
            <Grid item xs={4.5}>
              <Box
                sx={{
                  backgroundColor: row.isHeader ? '#ffe082' : '#ffffff',
                  color: '#1a237e',
                  p: 0.6,
                  fontSize: 12,
                  fontWeight: row.isHeader ? 'bold' : 'normal',
                  pl: row.indent ? 2.5 : 1,
                }}
              >
                {row.tooltip ? (
                  <Tooltip title={row.tooltip} arrow placement="bottom">
                    <span>{row.label}</span>
                  </Tooltip>
                ) : (
                  row.label
                )}
              </Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: row.isHeader ? '#ffe082' : '#e0e0e0',
                  color: '#1a237e',
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              >
                {row.toDate}
              </Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: row.isHeader ? '#ffe082' : '#e0e0e0',
                  color: '#1a237e',
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              >
                {row.atCompletion}
              </Box>
            </Grid>
          </Grid>
        ))}
      </Box>

      {/* Divider Separator Line */}
      <Box sx={{ height: '1px', my: 1 }} />

      {/* Delta Variations Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {tableData.deltas.map((delta) => (
          <Grid container spacing={1} alignItems="center" key={delta.id}>
            <Grid item xs={4.5}>
              <Box sx={{ backgroundColor: '#ffffff', color: '#1a237e', p: 0.6, fontSize: 12 }}>{delta.label}</Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: isNegative(delta.toDate) ? '#f44336' : '#1a237e',
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              >
                {delta.toDate}
              </Box>
            </Grid>
            <Grid item xs={3.75}>
              <Box
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: isNegative(delta.atCompletion) ? '#f44336' : '#1a237e',
                  p: 0.6,
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 'bold',
                }}
              >
                {delta.atCompletion}
              </Box>
            </Grid>
          </Grid>
        ))}
      </Box>
    </Paper>
  );
}

export const formatCurrency = (
  value: number | string | null | undefined,
  symbol: string | null | undefined
): string => {
  if (value === undefined || value === null) {
    return `0 ${symbol}`;
  }

  // 3. Formats with space separators and appends the dynamic symbol
  const formattedValue = value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${formattedValue} ${symbol}`;
};

export const transformIncomeData = (rawJson: any, symbol: any) => {
  const data = rawJson.completionList;
  if (!data) return null;

  return {
    purchasedTotal: formatCurrency(data.purchased, symbol),
    rows: [
      {
        id: 'p_assigned',
        label: 'Purchased (planned)',
        toDate: formatCurrency(data.to_date?.purchased_assigned, symbol),
        atCompletion: formatCurrency(data.at_completion?.purchased_assigned, symbol),
        isHeader: true,
        tooltip: 'Monthly amount of the Purchase Order defined by sales department.',
      },
      {
        id: 'baseline',
        label: 'Baseline',
        toDate: formatCurrency(data.to_date?.baseline, symbol),
        atCompletion: formatCurrency(data.at_completion?.baseline, symbol),
        indent: true,
        tooltip: 'Amount of the Workunits initially loaded in e-Delivery during the Quote creation.',
      },
      {
        id: 'forecasted',
        label: 'Forecasted',
        toDate: formatCurrency(data.to_date?.forecast, symbol),
        atCompletion: formatCurrency(data.at_completion?.forecast, symbol),
        indent: true,
        tooltip: 'Forecast = Baseline - Cancelled + Added - Postponed OUT + Postponed IN',
      },
      {
        id: 'accepted',
        label: 'Accepted client',
        toDate: formatCurrency(data.to_date?.accepted_client, symbol),
        atCompletion: formatCurrency(data.at_completion?.accepted_client, symbol),
        indent: true,
        tooltip: 'Amount of workunits accepted by the client',
      },
      {
        id: 'invoiced',
        label: 'Invoiced',
        toDate: formatCurrency(data.to_date?.invoiced, symbol),
        atCompletion: formatCurrency(data.at_completion?.invoiced, symbol),
        isHeader: true,
        tooltip: 'Actual amount invoiced to the customer.',
      },
    ],
    deltas: [
      {
        id: 'd_base',
        label: 'Delta "Forecasted - Baseline"',
        toDate: formatCurrency(data.to_date?.delta_forecast_baseline, symbol),
        atCompletion: formatCurrency(data.at_completion?.delta_forecast_baseline, symbol),
      },
      {
        id: 'd_purch',
        label: 'Delta "Forecasted - Purchased"',
        toDate: formatCurrency(data.to_date?.delta_forecast_purchased, symbol),
        atCompletion: formatCurrency(data.at_completion?.delta_forecast_purchased, symbol),
      },
      {
        id: 'd_inv',
        label: 'Delta "Invoiced - Purchased"',
        toDate: formatCurrency(data.to_date?.delta_invoiced_purchased, symbol),
        atCompletion: formatCurrency(data.at_completion?.delta_invoiced_purchased, symbol),
      },
    ],
  };
};
